import { sidebarOpenTabAtom, SidebarTab } from '@/atoms/persistedPreferencesAtoms';
import { sessionListHost } from '@/composition/hosts/session/session-list/session-list-host';
import { IS_CONSOLIDATED_SESSIONS_LIST_ENABLED } from '@/features/flags/legacy-flags';
import { useIsFhirAuth } from '@/hooks/integrations/useIsFhirAuth';
import { cn } from '@/lib/cn';
import { PANEL_WIDTH } from '@heidi/design-system/page-shell';
import { skipLinkTargetProps } from '@heidi/design-system/skip-link';
import { externalLaunchContextAtom } from '@heidi/integrations/ehr/data/atoms';
import { SessionsBarContent } from '@heidi/session';
import { useAtomValue } from 'jotai';

/**
 * Fixed-width sessions list panel for the left side.
 * Used when IS_SIDEBAR_PANEL_ENABLED is OFF on in-session DualPanelLayout routes.
 * Not resizable — visibility toggles via sidebarOpenTab (SESSION_LIST).
 */
export const SessionsListPanelFixed = () => {
  const isFhirAuth = useIsFhirAuth();
  const isGenericEhrLaunch = Boolean(
    useAtomValue(externalLaunchContextAtom)?.ehrProvider
  );
  const isEhrLaunch = isFhirAuth || isGenericEhrLaunch;
  const isConsolidated = IS_CONSOLIDATED_SESSIONS_LIST_ENABLED();
  const sidebarOpenTab = useAtomValue(sidebarOpenTabAtom);
  const isOpen = sidebarOpenTab === SidebarTab.SESSION_LIST;

  // Hide the sessions list when launched from any EHR integration (Epic FHIR or generic EHR like Healthdirect)
  if (isEhrLaunch) {
    return null;
  }

  return (
    <aside
      {...skipLinkTargetProps(
        'sidebar-session-list-panel',
        cn(
          'hidden h-full flex-col overflow-hidden overflow-y-auto transition-all duration-500 ease-out sm:flex',
          isConsolidated
            ? 'gap-3 border-r border-border bg-surface-accent'
            : 'gap-2 border-r border-border',
          isOpen
            ? isConsolidated
              ? 'p-2'
              : 'w-[220px] min-w-[220px] px-2 py-4'
            : 'w-0 min-w-0 border-r-0 px-0 py-4'
        )
      )}
      style={
        isOpen && isConsolidated
          ? { width: PANEL_WIDTH, minWidth: PANEL_WIDTH }
          : undefined
      }
      onClick={(e) => e.stopPropagation()}
      // ARD-1977: this aside carries the panel's width transition; the global
      // frame sampler reports its open/close under this name.
      data-frame-surface="sidebar_panel"
      data-testid="sidebar-session-list-panel"
    >
      <div
        className={cn(
          'flex h-full shrink-0 flex-col transition-transform duration-500 ease-out',
          isConsolidated ? 'w-full' : 'min-w-[200px]'
        )}
      >
        <SessionsBarContent host={sessionListHost} />
      </div>
    </aside>
  );
};
